import { loadImage } from './watermark';

export const MAX_SPONSORS = 20;
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;
export const MAX_LOGO_SIDE = 4096;

export const ownsEvent = (event, user) => {
    const owner = typeof event?.userId === 'string' ? event.userId.replace(/^USER#/, '') : '';
    return !!owner && !!user?.id && owner === user.id;
};

export const createSponsorId = () => {
    if (window.crypto.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const validSponsorWebsite = (value) => {
    if (!value?.trim()) return true;
    // Reject embedded whitespace/control characters instead of letting URL remove them.
    if (/\s/.test(value.trim()) || Array.from(value).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return false;
    try {
        const url = new URL(value.trim());
        return /^https?:\/\//i.test(value.trim()) && ['https:', 'http:'].includes(url.protocol)
            && !!url.hostname && !url.username && !url.password;
    } catch { return false; }
};

export const sponsorErrors = (sponsor) => ({
    name: !sponsor.name.trim() || sponsor.name.trim().length > 100 ? 'name' : null,
    logo: !sponsor.logoUrl && !sponsor.imageBase64 ? 'logo' : null,
    websiteUrl: validSponsorWebsite(sponsor.websiteUrl) ? null : 'website',
});

export const validSponsors = (sponsors) => sponsors.length <= MAX_SPONSORS
    && sponsors.every(sponsor => !Object.values(sponsorErrors(sponsor)).some(Boolean));

export const moveSponsor = (sponsors, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sponsors.length) return sponsors;
    const next = [...sponsors];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
};

const readDataUrl = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('image'));
    reader.readAsDataURL(file);
});

// Inspect chunk boundaries, not text inside compressed image data. APNG and animated
// WebP are rejected before the browser decodes only their first frame.
export const inspectSponsorBytes = (bytes) => {
    const text = (start, length) => String.fromCharCode(...bytes.slice(start, start + length));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.length >= 24 && text(0, 8) === '\x89PNG\r\n\x1a\n') {
        for (let offset = 8; offset + 12 <= bytes.length;) {
            const size = view.getUint32(offset);
            if (size > bytes.length - offset - 12) throw new Error('image');
            if (text(offset + 4, 4) === 'acTL') throw new Error('animated');
            offset += size + 12;
        }
        if (view.getUint32(16) > MAX_LOGO_SIDE || view.getUint32(20) > MAX_LOGO_SIDE) throw new Error('dimensions');
        return 'image/png';
    }
    if (bytes.length >= 12 && text(0, 4) === 'RIFF' && text(8, 4) === 'WEBP') {
        for (let offset = 12; offset + 8 <= bytes.length;) {
            const size = view.getUint32(offset + 4, true);
            if (size > bytes.length - offset - 8) throw new Error('image');
            const chunk = text(offset, 4);
            if (chunk === 'ANIM' || chunk === 'ANMF' || (chunk === 'VP8X' && size > 0 && (bytes[offset + 8] & 2))) throw new Error('animated');
            offset += size + 8 + (size % 2);
        }
        return 'image/webp';
    }
    if (bytes.length >= 3 && bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg';
    throw new Error('image');
};

export const inspectSponsorFile = async (file) => {
    if (!file.size || file.size > MAX_LOGO_BYTES) throw new Error('size');
    const dataUrl = await readDataUrl(file);
    const imageBase64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const bytes = Uint8Array.from(atob(imageBase64), character => character.charCodeAt(0));
    const type = inspectSponsorBytes(bytes);
    if (file.type && type !== file.type) throw new Error('image');
    let image;
    try { image = await loadImage(file); } catch { throw new Error('image'); }
    if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth > MAX_LOGO_SIDE || image.naturalHeight > MAX_LOGO_SIDE) throw new Error('dimensions');
    return { imageBase64, previewUrl: `data:${type};base64,${imageBase64}` };
};
